// src/resources/reports/ReportPushNotifications/FunnelCards.tsx
//
// Funil do período (§4 bloco 1 do spec original de 2026-09-17): Enviadas →
// Confirmadas pelo app → Lidas → Clicadas → Aberturas influenciadas, com a
// taxa sobre enviadas e a variação contra o período anterior — é a resposta
// direta ao problema do §1 daquele spec ("5.763 disparos" não diz nada;
// "82% de entrega, subindo 3%" diz). Rótulos e o card de "Aberturas
// influenciadas"/"Engajamento total" vêm do spec 2026-09-18-abertura-influenciada-push-design.md
// (pontuax-backend) — ver §3.
import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  DeltaPp, FunnelTotals, engagementRate, fmtDelta, fmtInt, fmtPctSmall, influencedOpenRate,
} from './types';

const BLUE = '#3b82f6';
const PURPLE = '#8b5cf6';
const LILAC = '#a78bfa';
const TEAL = '#0d9488';
const INK = '#0f172a';
const GRAY = '#94a3b8';
const SUCCESS = '#16a34a';
const DANGER = '#ef4444';

interface Step {
  label: string;
  // Legenda curta embaixo do rótulo: "Confirmadas pelo app" e "Aberturas
  // influenciadas" não se explicam sozinhas, e a nota de rodapé fica longe
  // demais do número pra resolver a dúvida na hora em que ela aparece.
  caption: string;
  value: number;
  rate: number | null; // sobre `sent`, null na primeira etapa (Enviadas não tem taxa)
  deltaPp: number | null;
  color: string;
}

const StepCard = ({ step }: { step: Step }) => (
  <Box sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: 3, p: 1.75, minWidth: 0 }}>
    <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: GRAY }}>
      {step.label}
    </Typography>
    <Typography sx={{ fontSize: 10.5, color: '#94a3b8', lineHeight: 1.3, mt: 0.25, minHeight: 27 }}>
      {step.caption}
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mt: 0.5 }}>
      <Typography sx={{ fontSize: 24, fontWeight: 800, color: INK }}>{fmtInt(step.value)}</Typography>
      <Box component="span" sx={{ width: 8, height: 8, borderRadius: 999, bgcolor: step.color, mb: 0.5 }} />
    </Box>
    {step.rate !== null && (
      <Typography sx={{ fontSize: 12.5, color: '#64748b', mt: 0.25 }}>
        <b style={{ color: INK }}>{fmtPctSmall(step.rate)}</b> sobre enviadas
      </Typography>
    )}
    {step.deltaPp !== null && (
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: step.deltaPp >= 0 ? SUCCESS : DANGER, mt: 0.25 }}>
        {fmtDelta(step.deltaPp)} vs período anterior
      </Typography>
    )}
  </Box>
);

const FunnelCards = ({ funnel, deltaPp }: { funnel: FunnelTotals; deltaPp: DeltaPp }) => {
  const steps: Step[] = [
    { label: 'Enviadas', caption: 'saíram para os aparelhos da rede', value: funnel.sent, rate: null, deltaPp: null, color: BLUE },
    // Renomeado de "Entregues" (§3 item 1 do spec): no Android com o app fechado, o sistema
    // exibe a push sem acordar o handler — esse número NUNCA passa por lá, então não é taxa de
    // entrega, é só "o app confirmou". Ver nota de rodapé no index.tsx.
    { label: 'Confirmadas pelo app', caption: 'o aplicativo avisou que recebeu — não é taxa de entrega', value: funnel.delivered, rate: funnel.deliveryRate, deltaPp: deltaPp.deliveryRate, color: PURPLE },
    { label: 'Lidas', caption: 'o cliente tocou na notificação', value: funnel.read, rate: funnel.readRate, deltaPp: deltaPp.readRate, color: LILAC },
    { label: 'Clicadas', caption: 'tocou e caiu na tela que a mensagem prometia', value: funnel.clicked, rate: funnel.clickRate, deltaPp: deltaPp.clickRate, color: '#c4b5fd' },
    // Card novo (§3 item 2 do spec): recebeu, não tocou, abriu o app em até 24h — o resultado que
    // o time de marketing entrega e que o funil acima, sozinho, não enxerga.
    {
      label: 'Aberturas influenciadas',
      caption: 'recebeu, não tocou, e abriu o app em até 24 horas',
      value: funnel.influencedOpens ?? 0,
      rate: influencedOpenRate(funnel),
      deltaPp: deltaPp.influencedOpenRate,
      color: TEAL,
    },
  ];

  const engagement = engagementRate(funnel);

  return (
    <Box sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: 2, p: 2, mb: 2 }}>
      <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Funil do período</Typography>
      <Typography sx={{ fontSize: 12, color: '#64748b', mb: 1.5 }}>
        {fmtInt(funnel.messages)} mensagens em {fmtInt(funnel.dispatches)} disparos
        {funnel.errors > 0 && <> · {fmtInt(funnel.errors)} com erro de envio</>}
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(5, 1fr)' }, gap: 1.25 }}>
        {steps.map(step => <StepCard key={step.label} step={step} />)}
      </Box>

      {/* §3 item 3 do spec: (lidas + aberturas influenciadas) / enviadas — o número que mede o
          trabalho do time sem punir quem volta pelo ícone em vez de tocar na notificação. Linha
          separada (não mais um card pequeno): é o resultado mais importante da tela. */}
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 1.5, pt: 1.5, borderTop: '1px dashed #e2e8f0' }}>
        <Typography sx={{ fontSize: 12.5, color: '#64748b' }}>Engajamento total do time</Typography>
        <Typography sx={{ fontSize: 18, fontWeight: 800, color: TEAL }}>{fmtPctSmall(engagement)}</Typography>
        {deltaPp.engagementRate !== null && (
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: deltaPp.engagementRate >= 0 ? SUCCESS : DANGER }}>
            {fmtDelta(deltaPp.engagementRate)} vs período anterior
          </Typography>
        )}
        <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>(lidas + aberturas influenciadas) / enviadas</Typography>
      </Box>
    </Box>
  );
};

export default FunnelCards;
